import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AddPhotoAlternateOutlined from '@mui/icons-material/AddPhotoAlternateOutlined';
import CheckCircle from '@mui/icons-material/CheckCircle';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import DragIndicator from '@mui/icons-material/DragIndicator';
import TranslateIcon from '@mui/icons-material/Translate';
import WarningAmberOutlined from '@mui/icons-material/WarningAmberOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { BookLanguageRow } from '../../Settings/lists/singleTableListSupport';
import { bookLanguageFlagDisplaySrc } from '../../Settings/lists/singleTableListSupport';
import BookImageAspectCropPreview from './BookImageAspectCropPreview';
import {
  BOOK_IMAGE_ACCEPT,
  BOOK_PAGE_IMAGE_HEIGHT_PX,
  BOOK_PAGE_IMAGE_PREVIEW_SX,
  BOOK_PAGE_IMAGE_WIDTH_PX,
} from './constants';
import { defaultPageLangContent } from './model';
import type { BookPageDraft } from './types';

const PAGE_IMAGE_APP_CROP_CAPTION = `Сүүдэртэй хэсэг нь апп дээр ${BOOK_PAGE_IMAGE_WIDTH_PX}×${BOOK_PAGE_IMAGE_HEIGHT_PX} харьцааны хуудасны зургийн цонхонд бүрэн харагдахгүй; төвд таслагдах хэсгийг харуулна.`;

function getPageCompletenessIssues(page: BookPageDraft, enabledLanguages: BookLanguageRow[]) {
  const missingImage = !(page.image_file || page.image_path.trim());
  const langsMissingText = enabledLanguages.filter(
    (lang) => !(page.byLang[lang.id]?.full_text ?? '').trim(),
  );
  return {
    hasIssue: missingImage || langsMissingText.length > 0,
    missingImage,
    langsMissingText,
  };
}

type SortablePageListRowProps = {
  page: BookPageDraft;
  selectedPageClientId: string | null;
  onSelectPage: (clientId: string) => void;
  onRequestDeletePage: (clientId: string) => void;
  hasCompletenessWarning: boolean;
};

function SortablePageListRow({
  page,
  selectedPageClientId,
  onSelectPage,
  onRequestDeletePage,
  hasCompletenessWarning,
}: SortablePageListRowProps) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: page.clientId,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { opacity: 0.55 } : {}),
  };

  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      disablePadding
      secondaryAction={
        <Tooltip title="Хуудсыг устгах">
          <IconButton
            size="small"
            aria-label="Хуудсыг устгах"
            color="error"
            sx={{ mr: 0.5 }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onRequestDeletePage(page.clientId);
            }}
          >
            <DeleteOutline fontSize="small" />
          </IconButton>
        </Tooltip>
      }
    >
      <Box sx={{ display: 'flex', alignItems: 'stretch', width: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <Tooltip title="Чирж дараалал солих">
            <IconButton
              ref={setActivatorNodeRef}
              size="small"
              aria-label="Чирж дараалал солих"
              {...listeners}
              {...attributes}
              sx={{
                cursor: 'grab',
                borderRadius: 0,
                color: 'text.secondary',
                '&:active': { cursor: 'grabbing' },
              }}
            >
              <DragIndicator fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <ListItemButton
          selected={page.clientId === selectedPageClientId}
          onClick={() => onSelectPage(page.clientId)}
          sx={{ flex: 1, minWidth: 0, pr: 7 }}
        >
          <ListItemText
            primary={
              <Stack direction="row" alignItems="center" spacing={0.5} component="span" sx={{ minWidth: 0 }}>
                <Typography component="span" variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                  Хуудас № {page.page_number}
                </Typography>
                {hasCompletenessWarning ? (
                  <Tooltip title="Зураг эсвэл идэвхтэй хэл бүрийн текст дутуу">
                    <WarningAmberOutlined
                      sx={{ fontSize: '1.125rem', color: 'warning.main', flexShrink: 0 }}
                      aria-label="Зураг эсвэл текст дутуу"
                    />
                  </Tooltip>
                ) : null}
              </Stack>
            }
          />
        </ListItemButton>
      </Box>
    </ListItem>
  );
}

type BookFormStepPagesProps = {
  enabledLanguages: BookLanguageRow[];
  sortedPages: BookPageDraft[];
  selectedPageClientId: string | null;
  onSelectPage: (clientId: string) => void;
  selectedPage: BookPageDraft | null;
  canTranslatePageFromEn: boolean;
  pageTranslating: boolean;
  pageTranslateError: string | null;
  onDismissPageTranslateError: () => void;
  onTranslatePageTextsFromEn: () => void;
  addPage: () => void;
  duplicateSelectedPage: () => void;
  deletePageByClientId: (clientId: string) => void;
  onReorderPages: (orderedClientIds: string[]) => void;
  updatePageByClientId: (clientId: string, patch: Partial<BookPageDraft>) => void;
  setPageFullText: (clientId: string, langId: string, value: string) => void;
  stepNavigation: ReactNode;
};

export default function BookFormStepPages({
  enabledLanguages,
  sortedPages,
  selectedPageClientId,
  onSelectPage,
  selectedPage,
  canTranslatePageFromEn,
  pageTranslating,
  pageTranslateError,
  onDismissPageTranslateError,
  onTranslatePageTextsFromEn,
  addPage,
  duplicateSelectedPage,
  deletePageByClientId,
  onReorderPages,
  updatePageByClientId,
  setPageFullText,
  stepNavigation,
}: BookFormStepPagesProps) {
  const pageImageInputRef = useRef<HTMLInputElement>(null);
  const [pageMediaTab, setPageMediaTab] = useState(0);

  useEffect(() => {
    setPageMediaTab(0);
  }, [selectedPageClientId]);

  const hasPageImage = Boolean(
    selectedPage && (selectedPage.image_file || selectedPage.image_path.trim()),
  );
  const hasPageVideo = Boolean(selectedPage && selectedPage.video_path.trim());

  const [activeDragClientId, setActiveDragClientId] = useState<string | null>(null);
  const [deleteConfirmClientId, setDeleteConfirmClientId] = useState<string | null>(null);

  const pageClientIds = useMemo(() => sortedPages.map((p) => p.clientId), [sortedPages]);

  const pagePendingDelete =
    deleteConfirmClientId != null
      ? sortedPages.find((p) => p.clientId === deleteConfirmClientId)
      : undefined;

  useEffect(() => {
    if (deleteConfirmClientId != null && !sortedPages.some((p) => p.clientId === deleteConfirmClientId)) {
      setDeleteConfirmClientId(null);
    }
  }, [deleteConfirmClientId, sortedPages]);

  const handleRequestDeletePage = useCallback((clientId: string) => {
    setDeleteConfirmClientId(clientId);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteConfirmClientId(null);
  }, []);

  const handleConfirmDeletePage = useCallback(() => {
    if (deleteConfirmClientId == null) return;
    deletePageByClientId(deleteConfirmClientId);
    setDeleteConfirmClientId(null);
  }, [deleteConfirmClientId, deletePageByClientId]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragClientId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveDragClientId(null);
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const ids = sortedPages.map((p) => p.clientId);
      const oldIndex = ids.indexOf(String(active.id));
      const newIndex = ids.indexOf(String(over.id));
      if (oldIndex < 0 || newIndex < 0) return;
      onReorderPages(arrayMove([...ids], oldIndex, newIndex));
    },
    [onReorderPages, sortedPages],
  );

  const handleDragCancel = useCallback(() => {
    setActiveDragClientId(null);
  }, []);

  const activeDragPage = activeDragClientId
    ? sortedPages.find((p) => p.clientId === activeDragClientId)
    : null;

  const selectedPageCompleteness = useMemo(
    () => (selectedPage ? getPageCompletenessIssues(selectedPage, enabledLanguages) : null),
    [selectedPage, enabledLanguages],
  );

  const pageCompletenessWarningByClientId = useMemo(() => {
    const m = new Map<string, boolean>();
    for (const p of sortedPages) {
      m.set(p.clientId, getPageCompletenessIssues(p, enabledLanguages).hasIssue);
    }
    return m;
  }, [sortedPages, enabledLanguages]);

  return (
    <Box>
      {enabledLanguages.length === 0 ? (
        <Alert severity="info">Эхний алхамд дор хаяж нэг хэл идэвхжүүлнэ үү.</Alert>
      ) : (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Одоо байгаа хуудсаас сонгоно, эсвэл шинэ хуудас нэмнэ. Хуудас бүрийн зургийг{' '}
            {`${BOOK_PAGE_IMAGE_WIDTH_PX}×${BOOK_PAGE_IMAGE_HEIGHT_PX}`} пиксел (өргөн {BOOK_PAGE_IMAGE_WIDTH_PX}{' '}
            пиксел) хэмжээтэй бэлтгэнэ үү; видео нь
            сонголттой. Медиа бүх хэлд ижил; хуудасны бүтэн текстийг хэл бүрээр оруулна.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            <Button type="button" variant="contained" size="small" onClick={addPage}>
              Шинэ хуудас нэмэх
            </Button>
            <Button
              type="button"
              variant="outlined"
              size="small"
              disabled={!selectedPage}
              onClick={duplicateSelectedPage}
            >
              Сонгосон хуудсыг хуулах
            </Button>
          </Stack>

          {sortedPages.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Хуудас алга. «Шинэ хуудас нэмэх» дарна уу.
            </Typography>
          ) : (
            <>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Хуудас сонгох
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                Зүүн талын зураасан товчоор чирж дарааллыг солино. Баруун талын устгах товчоор хуудсыг хасна.
              </Typography>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDragCancel={handleDragCancel}
              >
                <SortableContext items={pageClientIds} strategy={verticalListSortingStrategy}>
                  <List dense disablePadding sx={{ border: 1, borderColor: 'divider', borderRadius: 1, mb: 2 }}>
                    {sortedPages.map((p) => (
                      <SortablePageListRow
                        key={p.clientId}
                        page={p}
                        selectedPageClientId={selectedPageClientId}
                        onSelectPage={onSelectPage}
                        onRequestDeletePage={handleRequestDeletePage}
                        hasCompletenessWarning={pageCompletenessWarningByClientId.get(p.clientId) ?? false}
                      />
                    ))}
                  </List>
                </SortableContext>
                <DragOverlay dropAnimation={null}>
                  {activeDragPage ? (
                    <Box
                      sx={{
                        px: 2,
                        py: 1.25,
                        bgcolor: 'background.paper',
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        boxShadow: 3,
                        maxWidth: 320,
                      }}
                    >
                      <Typography variant="body2">Хуудас № {activeDragPage.page_number}</Typography>
                    </Box>
                  ) : null}
                </DragOverlay>
              </DndContext>

              <Dialog
                open={deleteConfirmClientId != null}
                onClose={handleCloseDeleteDialog}
                aria-labelledby="book-page-delete-dialog-title"
                aria-describedby="book-page-delete-dialog-description"
              >
                <DialogTitle id="book-page-delete-dialog-title">Хуудсыг хасах уу?</DialogTitle>
                <DialogContent>
                  <DialogContentText id="book-page-delete-dialog-description" component="div">
                    Энэ хуудсыг жагсаалтаас хасахдаа итгэлтэй байна уу? Түүний зураг, видео, текстийн оруулга устгагдана.
                    {pagePendingDelete != null && (
                      <Box
                        component="span"
                        sx={{ display: 'block', mt: 1, fontWeight: 600 }}
                      >
                        Хуудас № {pagePendingDelete.page_number}
                      </Box>
                    )}
                  </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                  <Button type="button" onClick={handleCloseDeleteDialog}>
                    Болих
                  </Button>
                  <Button type="button" color="error" variant="contained" onClick={handleConfirmDeletePage}>
                    Хасах
                  </Button>
                </DialogActions>
              </Dialog>

              {selectedPage && (
                <>
                  <input
                    ref={pageImageInputRef}
                    type="file"
                    accept={BOOK_IMAGE_ACCEPT}
                    hidden
                    onChange={(e) => {
                      const f = e.target.files?.[0] ?? null;
                      updatePageByClientId(selectedPage.clientId, { image_file: f });
                      e.target.value = '';
                    }}
                  />

                  {selectedPageCompleteness?.hasIssue ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="body2" component="div" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Хуудас бүрэн биш байна
                      </Typography>
                      {selectedPageCompleteness.missingImage ? (
                        <Typography variant="body2" component="div">
                          Зураг оруулна уу.
                        </Typography>
                      ) : null}
                      {selectedPageCompleteness.langsMissingText.length > 0 ? (
                        <Typography variant="body2" component="div">
                          Идэвхтэй хэл бүрээр бүтэн текст оруулна уу. Дутуу:{' '}
                          {selectedPageCompleteness.langsMissingText.map((l) => l.name || l.id).join(', ')}.
                        </Typography>
                      ) : null}
                    </Alert>
                  ) : null}

                  <Tabs
                    value={pageMediaTab}
                    onChange={(_, v) => setPageMediaTab(v)}
                    sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                    variant="fullWidth"
                  >
                    <Tab
                      id="book-page-media-tab-image"
                      aria-controls="book-page-media-panel-image"
                      label={
                        <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5} component="span">
                          <Typography component="span" variant="inherit">
                            Зураг
                          </Typography>
                          {hasPageImage ? (
                            <CheckCircle
                              sx={{ fontSize: '1.125rem', color: 'success.main' }}
                              aria-label="Зураг байна"
                            />
                          ) : null}
                        </Stack>
                      }
                    />
                    <Tab
                      id="book-page-media-tab-video"
                      aria-controls="book-page-media-panel-video"
                      label={
                        <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.5} component="span">
                          <Typography component="span" variant="inherit">
                            Видео
                          </Typography>
                          {hasPageVideo ? (
                            <CheckCircle
                              sx={{ fontSize: '1.125rem', color: 'success.main' }}
                              aria-label="Видео зам бөглөгдсөн"
                            />
                          ) : null}
                        </Stack>
                      }
                    />
                  </Tabs>

                  {pageMediaTab === 0 && (
                    <Stack
                      spacing={2}
                      role="tabpanel"
                      id="book-page-media-panel-image"
                      aria-labelledby="book-page-media-tab-image"
                    >
                      <Typography variant="caption" color="text.secondary" display="block">
                        Зургийн хэмжээ: {`${BOOK_PAGE_IMAGE_WIDTH_PX}×${BOOK_PAGE_IMAGE_HEIGHT_PX}`} пиксел (өргөн{' '}
                        {BOOK_PAGE_IMAGE_WIDTH_PX}). Доорх урьдчилан харах нь суурь харьцааг хадгална (сунгахгүй).
                      </Typography>
                      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button
                          type="button"
                          variant="outlined"
                          size="small"
                          startIcon={<AddPhotoAlternateOutlined />}
                          onClick={() => pageImageInputRef.current?.click()}
                        >
                          Зураг сонгох
                        </Button>
                        {(selectedPage.image_file || selectedPage.image_path.trim()) && (
                          <Button
                            type="button"
                            size="small"
                            onClick={() =>
                              updatePageByClientId(selectedPage.clientId, {
                                image_file: null,
                                image_path: '',
                              })
                            }
                          >
                            Арилгах
                          </Button>
                        )}
                      </Stack>
                      <Box
                        sx={{
                          width: 1,
                          maxWidth: 1,
                          alignSelf: 'stretch',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'flex-start',
                          bgcolor: 'action.hover',
                          borderRadius: 1,
                          border: 1,
                          borderColor: 'divider',
                          p: 0.5,
                        }}
                      >
                        <BookImageAspectCropPreview
                          file={selectedPage.image_file}
                          storageKey={selectedPage.image_path}
                          imageSx={BOOK_PAGE_IMAGE_PREVIEW_SX}
                          frameWidthPx={BOOK_PAGE_IMAGE_WIDTH_PX}
                          frameHeightPx={BOOK_PAGE_IMAGE_HEIGHT_PX}
                          caption={PAGE_IMAGE_APP_CROP_CAPTION}
                        />
                      </Box>
                      {/* <TextField
                        label="Зургийн S3 түлхүүр"
                        value={selectedPage.image_path}
                        onChange={(e) => updatePageByClientId(selectedPage.clientId, { image_path: e.target.value })}
                        fullWidth
                        size="small"
                      /> */}
                    </Stack>
                  )}

                  {pageMediaTab === 1 && (
                    <Stack
                      spacing={2}
                      role="tabpanel"
                      id="book-page-media-panel-video"
                      aria-labelledby="book-page-media-tab-video"
                    >
                      <TextField
                        label="Видео зам"
                        value={selectedPage.video_path}
                        onChange={(e) => updatePageByClientId(selectedPage.clientId, { video_path: e.target.value })}
                        fullWidth
                        size="small"
                        helperText="Ихэвчлэн хоосон үлдээнэ. Шаардлагатай бол S3 эсвэл гадаад видеоны зам оруулна."
                      />
                    </Stack>
                  )}

                  <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                    Хуудасны текст (идэвхтэй хэл бүр)
                  </Typography>

                  {canTranslatePageFromEn && (
                    <Box sx={{ mb: 1 }}>
                      <Button
                        type="button"
                        variant="outlined"
                        size="small"
                        startIcon={
                          pageTranslating ? <CircularProgress size={16} color="inherit" /> : <TranslateIcon />
                        }
                        disabled={pageTranslating}
                        onClick={() => void onTranslatePageTextsFromEn()}
                      >
                        Энэ хуудсын текстийг англиас бусад хэл рүү орчуулах
                      </Button>
                    </Box>
                  )}
                  {pageTranslateError && (
                    <Alert severity="error" onClose={onDismissPageTranslateError} sx={{ mb: 1 }}>
                      {pageTranslateError}
                    </Alert>
                  )}

                  <Stack spacing={2}>
                    {enabledLanguages.map((lang) => {
                      const flagSrc = bookLanguageFlagDisplaySrc(lang);
                      const lc = selectedPage.byLang[lang.id] ?? defaultPageLangContent();
                      const langTextMissing =
                        selectedPageCompleteness?.langsMissingText.some((l) => l.id === lang.id) ?? false;
                      return (
                        <Stack key={lang.id} spacing={1}>
                          <Box
                            sx={{
                              width: 48,
                              height: 32,
                              flexShrink: 0,
                              borderRadius: 0.5,
                              border: 1,
                              borderColor: 'divider',
                              overflow: 'hidden',
                              bgcolor: 'action.hover',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {flagSrc ? (
                              <Box
                                component="img"
                                src={flagSrc}
                                alt=""
                                sx={{ width: 1, height: 1, objectFit: 'cover', display: 'block' }}
                              />
                            ) : (
                              <Typography variant="caption" color="text.secondary" sx={{ px: 0.5, fontSize: '0.7rem' }}>
                                {lang.id}
                              </Typography>
                            )}
                          </Box>
                          <TextField
                            value={lc.full_text}
                            onChange={(e) =>
                              setPageFullText(selectedPage.clientId, lang.id, e.target.value)
                            }
                            fullWidth
                            multiline
                            minRows={4}
                            size="small"
                            placeholder="Хуудасны бүтэн текст"
                            aria-label={`${lang.name} (${lang.id}) — хуудасны бүтэн текст`}
                            error={langTextMissing}
                            helperText={langTextMissing ? 'Бүтэн текст оруулна уу.' : undefined}
                          />
                        </Stack>
                      );
                    })}
                  </Stack>
                </>
              )}
            </>
          )}
        </>
      )}
      {stepNavigation}
    </Box>
  );
}
